import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Shield, MapPin, Globe, Clock, AlertCircle, CheckCircle, XCircle, Key, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';
import api from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';

const LoginLogs = () => {
  const [filters, setFilters] = useState({
    email: '',
    statusMethod: '',
    limit: 100,
    offset: 0,
    dropdownOpen: false,
    limitDropdownOpen: false
  });

  const [emailInput, setEmailInput] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');

  // Debounce email input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmail(emailInput);
      setFilters(prev => ({ ...prev, email: emailInput, offset: 0 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [emailInput]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.status-method-dropdown-container') && 
          !event.target.closest('.limit-dropdown-container')) {
        setFilters(prev => ({ ...prev, dropdownOpen: false, limitDropdownOpen: false }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Parse user agent to extract device, OS, and browser info
  const parseUserAgent = (userAgent, platformVersion = null) => {
    if (!userAgent) return { device: 'Unknown', os: 'Unknown', browser: 'Unknown' };

    let device = 'Desktop';
    let os = 'Unknown';
    let browser = 'Unknown';

    // BROWSER DETECTION - Works for all platforms
    if (userAgent.includes('Edg/')) {
      const match = userAgent.match(/Edg\/([\d.]+)/);
      browser = match ? `Edge ${match[1]}` : 'Edge';
    } else if (userAgent.includes('Chrome/') && !userAgent.includes('Edg')) {
      const match = userAgent.match(/Chrome\/([\d.]+)/);
      browser = match ? `Chrome ${match[1]}` : 'Chrome';
    } else if (userAgent.includes('Firefox/')) {
      const match = userAgent.match(/Firefox\/([\d.]+)/);
      browser = match ? `Firefox ${match[1]}` : 'Firefox';
    } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
      const match = userAgent.match(/Version\/([\d.]+)/);
      browser = match ? `Safari ${match[1]}` : 'Safari';
    } else if (userAgent.includes('OPR/') || userAgent.includes('Opera/')) {
      const match = userAgent.match(/(?:OPR|Opera)\/([\d.]+)/);
      browser = match ? `Opera ${match[1]}` : 'Opera';
    }

    // CRITICAL: Check for mobile devices FIRST before desktop OS
    // iPhone and iPad user agents contain "Mac OS X" but are NOT macOS devices
    
    // iOS Detection (iPhone/iPad/iPod) - MUST come before Mac OS X check
    if (userAgent.includes('iPhone')) {
      device = 'iPhone';
      const match = userAgent.match(/OS ([\d_]+)/);
      if (match) {
        const version = match[1].replace(/_/g, '.');
        os = `iOS ${version}`;
      } else {
        os = 'iOS';
      }
    }
    else if (userAgent.includes('iPad')) {
      device = 'iPad';
      const match = userAgent.match(/OS ([\d_]+)/);
      if (match) {
        const version = match[1].replace(/_/g, '.');
        os = `iPadOS ${version}`;
      } else {
        os = 'iPadOS';
      }
    }
    else if (userAgent.includes('iPod')) {
      device = 'iPod Touch';
      const match = userAgent.match(/OS ([\d_]+)/);
      if (match) {
        const version = match[1].replace(/_/g, '.');
        os = `iOS ${version}`;
      } else {
        os = 'iOS';
      }
    }
    // Android Detection - Check before Linux
    else if (userAgent.includes('Android')) {
      const versionMatch = userAgent.match(/Android ([\d.]+)/);
      os = versionMatch ? `Android ${versionMatch[1]}` : 'Android';
      
      // Determine device type
      if (userAgent.includes('Mobile')) {
        device = 'Android Phone';
        // Try to extract device model
        const modelMatch = userAgent.match(/Android.*;\s*([^)]+)\s+Build/);
        if (modelMatch && modelMatch[1]) {
          device = modelMatch[1].trim();
        }
      } else {
        device = 'Android Tablet';
        const modelMatch = userAgent.match(/Android.*;\s*([^)]+)\s+Build/);
        if (modelMatch && modelMatch[1]) {
          device = modelMatch[1].trim();
        }
      }
    }
    // Windows Detection
    else if (userAgent.includes('Windows NT 10.0')) {
      device = 'Windows PC';
      
      // CRITICAL: Use platformVersion from database if available
      // This is captured during login from navigator.userAgentData.platformVersion
      // Windows 11 has platform version >= 13.0.0
      // Windows 10 has platform version < 13.0.0
      if (platformVersion) {
        const versionParts = platformVersion.split('.');
        const majorVersion = parseInt(versionParts[0]);
        
        if (majorVersion >= 13) {
          os = 'Windows 11';
        } else {
          os = 'Windows 10';
        }
      } else {
        // Fallback: Use browser version heuristic
        const chromeMatch = userAgent.match(/Chrome\/([\d]+)/);
        const edgeMatch = userAgent.match(/Edg\/([\d]+)/);
        
        if (chromeMatch) {
          const version = parseInt(chromeMatch[1]);
          if (version >= 110) {
            os = 'Windows 11';
          } else if (version >= 96) {
            os = 'Windows 11';
          } else {
            os = 'Windows 10';
          }
        } else if (edgeMatch) {
          const version = parseInt(edgeMatch[1]);
          if (version >= 110) {
            os = 'Windows 11';
          } else if (version >= 96) {
            os = 'Windows 11';
          } else {
            os = 'Windows 10';
          }
        } else {
          // Unknown browser, default to Windows 11 for modern systems
          os = 'Windows 11';
        }
      }
    }
    else if (userAgent.includes('Windows NT 6.3')) {
      device = 'Windows PC';
      os = 'Windows 8.1';
    }
    else if (userAgent.includes('Windows NT 6.2')) {
      device = 'Windows PC';
      os = 'Windows 8';
    }
    else if (userAgent.includes('Windows NT 6.1')) {
      device = 'Windows PC';
      os = 'Windows 7';
    }
    else if (userAgent.includes('Windows')) {
      device = 'Windows PC';
      os = 'Windows';
    }
    // macOS Detection - AFTER iOS check
    else if (userAgent.includes('Macintosh') || userAgent.includes('Mac OS X')) {
      device = 'Mac';
      const match = userAgent.match(/Mac OS X ([\d_]+)/);
      if (match) {
        const version = match[1].replace(/_/g, '.');
        const parts = version.split('.');
        if (parts.length >= 2) {
          const major = parseInt(parts[0]);
          const minor = parseInt(parts[1]);
          // macOS version naming
          if (major === 10) {
            if (minor >= 15) os = `macOS Catalina ${version}`;
            else if (minor >= 14) os = `macOS Mojave ${version}`;
            else if (minor >= 13) os = `macOS High Sierra ${version}`;
            else os = `macOS ${version}`;
          } else if (major >= 11) {
            // macOS 11+ (Big Sur, Monterey, Ventura, Sonoma)
            if (major === 11) os = `macOS Big Sur ${version}`;
            else if (major === 12) os = `macOS Monterey ${version}`;
            else if (major === 13) os = `macOS Ventura ${version}`;
            else if (major === 14) os = `macOS Sonoma ${version}`;
            else os = `macOS ${version}`;
          } else {
            os = `macOS ${version}`;
          }
        } else {
          os = `macOS ${version}`;
        }
      } else {
        os = 'macOS';
      }
    }
    // Chrome OS
    else if (userAgent.includes('CrOS')) {
      device = 'Chromebook';
      os = 'Chrome OS';
    }
    // Linux
    else if (userAgent.includes('Linux')) {
      device = 'Linux PC';
      if (userAgent.includes('Ubuntu')) os = 'Ubuntu';
      else if (userAgent.includes('Fedora')) os = 'Fedora';
      else if (userAgent.includes('Debian')) os = 'Debian';
      else os = 'Linux';
    }

    return { device, os, browser };
  };

  // Normalize ISP names to be more user-friendly
  const normalizeISP = (isp) => {
    if (!isp) return 'Unknown';
    
    const ispMappings = {
      'CENTURYLINK-US-LEGACY-QWEST': 'Lumen Technologies (Quantum Fiber)',
      'CENTURYLINK': 'Lumen Technologies (CenturyLink)',
      'COMCAST-7922': 'Comcast (Xfinity)',
      'COMCAST': 'Comcast (Xfinity)',
      'ATT-INTERNET4': 'AT&T',
      'CHARTER-NET-HKY': 'Charter Communications (Spectrum)',
      'CHARTER': 'Charter Communications (Spectrum)',
      'VERIZON-FIOS': 'Verizon (Fios)',
      'VERIZON': 'Verizon',
      'COX-ATLANTA': 'Cox Communications',
      'COX': 'Cox Communications',
      'GOOGLE-FIBER': 'Google Fiber',
      'TMOBILE': 'T-Mobile',
      'SPRINT': 'T-Mobile (Sprint)',
      'OPTIMUM-WIFI': 'Optimum',
      'OPTIMUM': 'Optimum',
      'FRONTIER': 'Frontier Communications',
      'WINDSTREAM': 'Windstream',
      'MEDIACOM': 'Mediacom',
      'SUDDENLINK': 'Optimum (Suddenlink)',
      'COGENT': 'Cogent Communications',
      'HURRICANE': 'Hurricane Electric',
      'CLOUDFLARE': 'Cloudflare',
      'AMAZON': 'Amazon Web Services',
      'DIGITALOCEAN': 'DigitalOcean',
      'LINODE': 'Akamai (Linode)',
      'VULTR': 'Vultr',
      'OVH': 'OVH',
      'HETZNER': 'Hetzner'
    };

    // Check for exact matches first
    const upperISP = isp.toUpperCase();
    for (const [key, value] of Object.entries(ispMappings)) {
      if (upperISP.includes(key)) {
        return value;
      }
    }

    // Clean up the ISP name if no mapping found
    return isp
      .replace(/-US-LEGACY-QWEST/gi, '')
      .replace(/-7922/gi, '')
      .replace(/-INTERNET4/gi, '')
      .replace(/-NET-HKY/gi, '')
      .replace(/-ATLANTA/gi, '')
      .replace(/-WIFI/gi, '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Fetch login logs
  const { data: logsData, isLoading } = useQuery(
    ['login-logs', debouncedEmail, filters.statusMethod, filters.limit, filters.offset],
    async () => {
      const params = new URLSearchParams();
      if (debouncedEmail) params.append('email', debouncedEmail);
      if (filters.statusMethod) {
        // Convert statusMethod filter to status filter for backend
        if (filters.statusMethod === 'trusted_ip') {
          params.append('status', 'success');
        } else if (filters.statusMethod === '2fa_required') {
          params.append('status', '2fa_required,2fa_verified');
        } else {
          // Direct status filters (success, failed)
          params.append('status', filters.statusMethod);
        }
      }
      params.append('limit', filters.limit);
      params.append('offset', filters.offset);

      const response = await api.get(`/login-logs?${params.toString()}`);
      return response.data.data;
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      keepPreviousData: true // Keep previous data while fetching new data
    }
  );

  // Fetch login statistics
  const { data: statsData } = useQuery(
    'login-stats',
    async () => {
      const response = await api.get('/login-logs/stats?days=30');
      return response.data.data;
    },
    {
      refetchInterval: 60000 // Refresh every minute
    }
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-400" />;
      case '2fa_required':
        return <AlertCircle className="h-4 w-4 text-yellow-400" />;
      case '2fa_verified':
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-400';
      case 'failed':
        return 'text-red-400';
      case '2fa_required':
        return 'text-yellow-400';
      case '2fa_verified':
        return 'text-green-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'success':
        return 'Success';
      case 'failed':
        return 'Failed';
      case '2fa_required':
        return 'Pending';
      case '2fa_verified':
        return 'Success';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {statsData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Unique Users</p>
                <p className="text-2xl font-bold text-gray-100 mt-1">{statsData.uniqueUsers}</p>
              </div>
              <Shield className="h-8 w-8 text-purple-400" />
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Unique IPs</p>
                <p className="text-2xl font-bold text-gray-100 mt-1">{statsData.uniqueIPs}</p>
              </div>
              <Globe className="h-8 w-8 text-blue-400" />
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Successful Logins</p>
                <p className="text-2xl font-bold text-green-400 mt-1">
                  {statsData.statusStats.find(s => s.login_status === 'success')?.count || 0}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Failed Attempts</p>
                <p className="text-2xl font-bold text-red-400 mt-1">
                  {statsData.statusStats.find(s => s.login_status === 'failed')?.count || 0}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-6" style={{ overflow: 'visible' }}>
        <h3 className="text-lg font-semibold text-gray-100 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Email</label>
            <input
              type="text"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="Search by email..."
              className="w-full px-4 py-2 rounded-lg text-gray-100 placeholder-gray-400"
              style={{ 
                backgroundColor: '#0a0a0a',
                border: '1px solid #4a4a4a',
                outline: 'none',
                transition: 'box-shadow 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 2px #3b198e';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = '';
              }}
            />
          </div>

          <div className="status-method-dropdown-container" style={{ position: 'relative' }}>
            <label className="block text-sm text-gray-400 mb-2">Status/Method</label>
            <div
              onClick={() => setFilters({ ...filters, dropdownOpen: !filters.dropdownOpen, limitDropdownOpen: false })}
              style={{ 
                width: '100%',
                backgroundColor: '#0a0a0a', 
                border: '1px solid #4a4a4a',
                borderRadius: '8px',
                paddingTop: '0.5rem',
                paddingBottom: '0.5rem',
                paddingLeft: '1rem',
                paddingRight: '2.5rem',
                cursor: 'pointer',
                position: 'relative',
                color: '#ffffff',
                transition: 'border-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
            >
              {filters.statusMethod === '' ? 'All' : 
               filters.statusMethod === 'success' ? 'Success' :
               filters.statusMethod === 'failed' ? 'Failed' :
               filters.statusMethod === 'trusted_ip' ? 'Trusted IP' :
               filters.statusMethod === '2fa_required' ? 'Pending' : 'All'}
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#ffffff" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: filters.dropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                  width: '1.25rem',
                  height: '1.25rem',
                  transition: 'transform 0.2s ease',
                  pointerEvents: 'none'
                }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            
            {filters.dropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '8px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  animation: 'dropdownSlide 0.2s ease-out'
                }}
              >
                {[
                  { value: '', label: 'All' },
                  { value: 'success', label: 'Success' },
                  { value: 'failed', label: 'Failed' },
                  { value: 'trusted_ip', label: 'Trusted IP' },
                  { value: '2fa_required', label: 'Pending' }
                ].map(option => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setFilters({ ...filters, statusMethod: option.value, offset: 0, dropdownOpen: false });
                    }}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      color: '#ffffff',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="limit-dropdown-container" style={{ position: 'relative' }}>
            <label className="block text-sm text-gray-400 mb-2">Records Per Page</label>
            <div
              onClick={() => setFilters({ ...filters, limitDropdownOpen: !filters.limitDropdownOpen, dropdownOpen: false })}
              style={{ 
                width: '100%',
                backgroundColor: '#0a0a0a', 
                border: '1px solid #4a4a4a',
                borderRadius: '8px',
                paddingTop: '0.5rem',
                paddingBottom: '0.5rem',
                paddingLeft: '1rem',
                paddingRight: '2.5rem',
                cursor: 'pointer',
                position: 'relative',
                color: '#ffffff',
                transition: 'border-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
            >
              {filters.limit} per page
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#ffffff" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: filters.limitDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                  width: '1.25rem',
                  height: '1.25rem',
                  transition: 'transform 0.2s ease',
                  pointerEvents: 'none'
                }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            
            {filters.limitDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '8px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  animation: 'dropdownSlide 0.2s ease-out'
                }}
              >
                {[50, 100, 200, 500].map(limitOption => (
                  <div
                    key={limitOption}
                    onClick={() => {
                      setFilters({ ...filters, limit: limitOption, offset: 0, limitDropdownOpen: false });
                    }}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      color: '#ffffff',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {limitOption} per page
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Login Logs Table */}
      <div className="card overflow-hidden">
        <div className="p-6 border-b border-gray-800">
          <h3 className="text-lg font-semibold text-gray-100">Login Records</h3>
          <p className="text-sm text-gray-400 mt-1">
            Showing {logsData?.logs?.length || 0} of {logsData?.pagination?.total || 0} records
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid #4a4a4a' }}>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Login #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  2FA Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Device/OS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  IP Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  ISP
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800" style={{ borderTop: '1px solid #4a4a4a' }}>
              {logsData?.logs?.map((log) => (
                <tr key={log.id} className="transition-colors" style={{ cursor: 'default' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1a1a1a'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-400 font-mono">
                      {log.session_id ? log.session_id.substring(0, 8) : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-300">
                      <Clock className="h-4 w-4 mr-2 text-gray-500" />
                      {format(new Date(log.created_at + 'Z'), 'MMM dd, h:mm a')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium text-gray-100">{log.discord_username}</div>
                      <div className="text-gray-400">{log.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(log.login_status)}
                      <span className={`ml-2 text-sm font-medium ${getStatusColor(log.login_status)}`}>
                        {getStatusLabel(log.login_status)}
                      </span>
                    </div>
                    {log.failure_reason && (
                      <div className="text-xs text-red-400 mt-1">{log.failure_reason}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {log.twofa_method === 'trusted_ip' && (
                      <div className="flex items-center text-sm">
                        <ShieldCheck className="h-4 w-4 mr-2 text-green-400" />
                        <span className="text-green-400">Trusted IP</span>
                      </div>
                    )}
                    {log.twofa_method === '2fa_required' && (
                      <div className="flex items-center text-sm">
                        <Shield className="h-4 w-4 mr-2 text-blue-400" />
                        <span className="text-blue-400">2FA Required</span>
                      </div>
                    )}
                    {!log.twofa_method && log.login_status !== 'failed' && (
                      <div className="flex items-center text-sm">
                        <Shield className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="text-gray-400">Unknown</span>
                      </div>
                    )}
                    {log.login_status === 'failed' && (
                      <div className="text-sm text-gray-500">N/A</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      {(() => {
                        const { device, os, browser } = parseUserAgent(log.user_agent, log.platform_version);
                        return (
                          <>
                            <div className="font-medium text-gray-100">{device}</div>
                            <div className="text-gray-400">{os}</div>
                            <div className="text-gray-500 text-xs mt-1">{browser}</div>
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300 font-mono">{log.ip_address}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 mr-2 text-gray-500 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        {log.city && log.region && log.country ? (
                          <>
                            <div className="text-gray-300">{log.city}, {log.region}</div>
                            <div className="text-gray-400">{log.country} ({log.country_code})</div>
                          </>
                        ) : (
                          <div className="text-gray-500">Unknown</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-400 max-w-xs truncate">
                      {normalizeISP(log.isp)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {logsData?.pagination && (
          <div className="p-4 border-t border-gray-800 flex items-center justify-between">
            <button
              onClick={() => setFilters({ ...filters, offset: Math.max(0, filters.offset - filters.limit) })}
              disabled={filters.offset === 0}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-gray-400">
              Page {Math.floor(filters.offset / filters.limit) + 1}
            </span>
            <button
              onClick={() => setFilters({ ...filters, offset: filters.offset + filters.limit })}
              disabled={!logsData.pagination.hasMore}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Top Countries */}
      {statsData?.countryStats && statsData.countryStats.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-100 mb-4">Top Countries (Last 30 Days)</h3>
          <div className="space-y-3">
            {statsData.countryStats.map((country, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{country.country_code}</span>
                  <span className="text-sm text-gray-300">{country.country}</span>
                </div>
                <span className="text-sm font-medium text-gray-100">{country.count} logins</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginLogs;
